import { LiveAnnouncer } from '@angular/cdk/a11y';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatChipEditedEvent, MatChipInputEvent } from '@angular/material/chips';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortable, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Observable, debounceTime, distinctUntilChanged, map, startWith } from 'rxjs';
import { MyErrorStateMatcher } from 'src/app/auth/login/login.component';
import { UserService } from 'src/app/shared/user.service';
import { Attributes } from '../../../shared/attributes.model';
import { ContractTypeService } from '../../../shared/contract-type.service';
import { DeleteDialogComponent } from '../../delete-dialog/delete-dialog.component';

@Component({
  selector: 'app-contract-type-form',
  templateUrl: './contract-type-form.component.html',
  styleUrls: ['./contract-type-form.component.css']
})
export class ContractTypeFormComponent implements OnInit, AfterViewInit {
  dataSource = new MatTableDataSource<any>();
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  displayedColumns: string[] = ['Label', 'Default Value', 'Section Name', 'Sequence Number', 'Seed Attribute', 'Last Updated', 'Action'];
  @ViewChild('sectionNameInput') sectionNameInput!: ElementRef<HTMLInputElement>;

  filteredOptions: Observable<string[]>;
  matcher = new MyErrorStateMatcher();
  id: any;
  mode: any;
  formLoading: boolean = false;
  contractForm: FormGroup;
  metadataForm: FormGroup;
  isLoading = false;
  dataTypes: any[] = [];
  contracts = [];
  contractFroms = [];
  metaDataList: any;
  contractType: any;
  attributeSequenceNumber: number = 0;
  viewMode: boolean = false;
  allMetaDataList: any;
  isSequenceNumberValid: boolean = true;
  sectionNames: string[] = [];
  showDropdown: boolean = false;
  values: string[] = [];
  addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  announcer = inject(LiveAnnouncer);
  isBooleanError: boolean = false;
  seedAttributes: any;
  isToggleLoading: { [key: string]: boolean } = {};


  constructor(public userService: UserService, public service: ContractTypeService, private router: Router, private route: ActivatedRoute, private formBuilder: FormBuilder, private toast: ToastrService, private dialog: MatDialog, private elementRef: ElementRef) {
    this.route.params.subscribe(params => {
      this.id = params['id'];
      this.mode = params['mode'];
    });

    if (this.mode)
      this.formLoading = true;

    this.contractForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      description: ['', [Validators.required]],
      version: [1],

    });

    this.metadataForm = this.formBuilder.group({
      label: ['', [Validators.required]],
      metaDataTypeId: [null, [Validators.required]],
      metaDataValues: [null],
      defaultValue: [''],
      isRequired: [true],
      isEditable: [true],
      isHidden: [false],
      helpText: [''],
      isDependent: [false],
      dependentOnAttributeId: [null],
      isInherited: [false],
      inheritedContractId: [{ value: null, disabled: true }],
      inheritedFrom: [{ value: null, disabled: true }],
      isDisplaySummary: [true],
      isEditablePostExecution: [true],
      isSearchEnabled: [true],
      sequenceNo: [1, [Validators.required, Validators.min(1)]],
      sectionName: ['', [Validators.required]],
      attributeId: [0],
      seedAttributeId: [0]
    });

    this.metadataForm.get('dependentOnAttributeId').disable();
    this.metadataForm.get('isDependent').disable();

    if (this.mode == 'view') {
      this.contractForm.disable();
      this.metadataForm.disable();
    }
  }

  onSectionNameChange(value: string) {
    this.filteredOptions = this.metadataForm.get('sectionName').valueChanges.pipe(
      startWith(value),
      map(val => this._filter(val || ''))
    );
  }

  async onSectionSelect(event) {
    this.metadataForm.get('sectionName').setValue(event.option.value);

    if (this.sectionNames) {
      this.filteredOptions = this.metadataForm.get('sectionName').valueChanges.pipe(
        startWith(''),
        map(value => this._filter(value || ''))
      );
    }
  }


  private _filter(value: string): any[] {
    const filterValue = value.toLowerCase();
    const results = this.sectionNames.filter(option => option.toLowerCase().includes(filterValue));

    return results;
  }

  async ngOnInit() {
    await this.service.getAttributeMetadataType().then(res => {
      this.dataTypes = res as Array<Object>;
    });

    await this.service.getContractList().then(res => {
      this.contracts = res as Array<Object>;
    });

    this.initializeContract();

    // Watching for changes in the sequenceNo field
    this.metadataForm.get('sequenceNo').valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe(value => {
        this.validateSequenceNumber();
      });

    this.metadataForm.get('metaDataTypeId').valueChanges
      .pipe(
        distinctUntilChanged()
      )
      .subscribe(val => {
        this.metaDataTypeTrigger();
      });
  }

  scrollToTop() {
    // Access the window object
    const windowRef = this.elementRef.nativeElement.ownerDocument.defaultView;

    // Scroll to the top
    if (windowRef) {
      windowRef.scrollTo({ top: 0, behavior: 'smooth' }); // Use 'auto' for instant scroll
    }
  }

  async toggleSeedAttribute(data) {
    if (this.mode == 'edit') {
      this.isToggleLoading[data.attributeId] = true;
      data.isHidden = data.isHidden === true ? false : true;
      await this.service.editContract(data, this.contractType).then((res) => {
        this.toast.success(data.label + ' has been updated successfully!', '');
        this.initializeContract(data.attributeId);
      });
    } else {
      const index = this.seedAttributes.findIndex(item => item.seedAttributeId === data.seedAttributeId);
      if (index !== -1)
        this.seedAttributes[index].isHidden = !this.seedAttributes[index].isHidden;
    }

  }

  metaDataTypeTrigger() {
    const supportedDataTypeIds = [2, 15, 12];
    if (supportedDataTypeIds.includes(this.metadataForm.get('metaDataTypeId').value)) {
      this.showDropdown = true;
      this.metadataForm.get('metaDataValues').setValidators(Validators.required);
      this.metadataForm.get('metaDataValues').updateValueAndValidity();
    } else {
      this.showDropdown = false;
      this.metadataForm.get('metaDataValues').setValue(null);
      this.values = [];
      this.metadataForm.get('metaDataValues').setValidators(null);
      this.metadataForm.get('metaDataValues').updateValueAndValidity();
    }

    if (this.metadataForm.get('metaDataTypeId').value == 12) {
      this.metadataForm.get('metaDataValues').setValue(null);
      this.values = [];
    }

    if (this.metadataForm.get('metaDataTypeId').value == 10) {
      this.metadataForm.get('isRequired').disable();
      this.metadataForm.get('isRequired').setValue(false);
      this.metadataForm.get('isEditable').disable();
      this.metadataForm.get('isEditable').setValue(false);
    } else {
      this.metadataForm.get('isRequired').enable();
      this.metadataForm.get('isEditable').enable();
    }

  }

  async initializeContract(id = null) {
    if (this.id) {

      await this.service.getAContract(this.id).then((res: any) => {
        this.contractType = res;
        this.metaDataList = res.attributes;
        this.allMetaDataList = res.attributes;

        this.metaDataList.forEach((attrib: any) => {
          attrib.updatedDate = moment(attrib.updatedDate).format('lll');

          this.attributeSequenceNumber = (attrib.sequenceNo > this.attributeSequenceNumber) ? (attrib.sequenceNo) : (this.attributeSequenceNumber);

          if (!this.sectionNames.includes(attrib.sectionName)) {
            this.sectionNames.push(attrib.sectionName);
          }
        });

        this.contractForm.patchValue(this.contractType);

      });
    } else {
      await this.service.getAttributeList().then((seedAttributes: any) => {
        seedAttributes.forEach((attribute: any) => {
          attribute.updatedDate = moment(attribute.updatedDate).format('lll');

          this.attributeSequenceNumber = (attribute.sequenceNo > this.attributeSequenceNumber) ? (attribute.sequenceNo) : (this.attributeSequenceNumber);

          if (!this.sectionNames.includes(attribute.sectionName)) {
            this.sectionNames.push(attribute.sectionName);
          }
        });
        this.seedAttributes = seedAttributes;
      });
    }

    if (this.metaDataList && this.seedAttributes) {
      this.dataSource.data = [...this.metaDataList, ...this.seedAttributes];
    } else if (this.metaDataList) {
      this.dataSource.data = this.metaDataList;
    } else if (this.seedAttributes) {
      this.dataSource.data = this.seedAttributes;
    } else {
      this.dataSource.data = [];
    }

    if (this.sectionNames) {
      this.filteredOptions = this.metadataForm.get('sectionName').valueChanges.pipe(
        startWith(''),
        map(value => this._filter(value || ''))
      );
    }

    if (this.attributeSequenceNumber) {
      this.metadataForm.get('sequenceNo').setValue(this.attributeSequenceNumber + 1);
    }

    if (this.id && this.mode != 'view')
      this.metadataForm.get('isDependent').enable();

    this.formLoading = false;
    this.isToggleLoading[id] = false;
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort
  }

  toggleDependent() {
    if (this.metadataForm.get('isDependent').value) {
      this.metadataForm.get('dependentOnAttributeId').enable();
      this.metadataForm.get('dependentOnAttributeId').setValidators(Validators.required);
      this.metadataForm.get('dependentOnAttributeId').updateValueAndValidity();
    } else {
      this.metadataForm.get('dependentOnAttributeId').disable();
      this.metadataForm.get('dependentOnAttributeId').setValue(null);
      this.metadataForm.get('dependentOnAttributeId').setValidators(null);
      this.metadataForm.get('dependentOnAttributeId').updateValueAndValidity();
    }
  }

  toggleRequiredHidden(value: string) {
    if (value === 'required' && this.metadataForm.get('isRequired').value && this.metadataForm.get('isHidden').value)
      this.metadataForm.get('isHidden').setValue(false);
    if (value === 'required' && this.metadataForm.get('isRequired').value && !this.metadataForm.get('isEditable').value)
      this.metadataForm.get('isEditable').setValue(true);
    if (value === 'hidden' && this.metadataForm.get('isRequired').value && this.metadataForm.get('isHidden').value)
      this.metadataForm.get('isRequired').setValue(false);
    if (value === 'editable' && this.metadataForm.get('isRequired').value && !this.metadataForm.get('isEditable').value)
      this.metadataForm.get('isRequired').setValue(false);
  }

  toggleInherited() {
    if (this.metadataForm.get('isInherited').value) {
      if (this.contracts) {
        this.metadataForm.get('inheritedContractId').enable();
        this.metadataForm.get('inheritedContractId').setValidators(Validators.required);
        this.metadataForm.get('inheritedContractId').updateValueAndValidity();
      } else {
        this.toast.warning('No Contracts Found', '');
        this.resetToggleInherit();
      }
    } else {
      this.resetToggleInherit();
    }
  }

  async selectedContract(event: any) {
    await this.service.getAContract(event.value).then((res: any) => {
      this.contractFroms = res.attributes as Array<object>;
    })
    if (this.contractFroms) {
      this.metadataForm.get('inheritedFrom').enable();
      this.metadataForm.get('inheritedFrom').setValidators(Validators.required);
      this.metadataForm.get('inheritedFrom').updateValueAndValidity();
    } else {
      this.toast.warning('No Data Type Found', '');
      this.resetToggleInherit();
    }
  }

  resetToggleInherit() {
    this.metadataForm.get('inheritedContractId').disable();
    this.metadataForm.get('inheritedFrom').disable();
    this.metadataForm.get('isInherited').setValue(false);
    this.metadataForm.get('inheritedContractId').setValue(null);
    this.metadataForm.get('inheritedFrom').setValue(null);
    this.metadataForm.get('inheritedContractId').setValidators(null);
    this.metadataForm.get('inheritedContractId').updateValueAndValidity();
    this.metadataForm.get('inheritedFrom').setValidators(null);
    this.metadataForm.get('inheritedFrom').updateValueAndValidity();
    this.contractFroms = [];
  }

  editMetaData(data, view = false) {
    this.metadataForm.patchValue(data);
    this.metaDataList = this.allMetaDataList;
    if (this.metadataForm.get('metaDataValues').value)
      this.values = this.metadataForm.get('metaDataValues').value.split(/\s*,\s*/);
    this.metaDataList = this.metaDataList.filter((option) => {
      return option.attributeId != this.metadataForm.get('attributeId').value
    });
    if (this.metadataForm.get('isInherited').value) {
      const selectedInheritContract = this.contracts.find(option => option.contractTypeId == data.inheritedContractId);
      if (selectedInheritContract) {
        this.metadataForm.get('inheritedContractId').setValue(selectedInheritContract.contractTypeId);
        this.selectedContractFrom(selectedInheritContract.contractTypeId, data, view);
        if (view)
          this.metadataForm.get('inheritedContractId').disable();
        else
          this.metadataForm.get('inheritedContractId').enable();
      }
    }

    if (this.metadataForm.get('isDependent').value) {
      const selectedDependent = this.metaDataList.find(option => option.attributeId == data.dependentOnAttributeId);
      if (selectedDependent) {
        this.metadataForm.get('dependentOnAttributeId').setValue(selectedDependent.attributeId);
        if (view)
          this.metadataForm.get('dependentOnAttributeId').disable();
        else
          this.metadataForm.get('dependentOnAttributeId').enable();
      }
    }

    if (view) {
      this.metadataForm.disable();
      this.viewMode = true;
    }
    else {
      this.metadataForm.enable();
      if (!this.metadataForm.get('isInherited').value) {
        this.metadataForm.get('inheritedContractId').disable();
        this.metadataForm.get('inheritedFrom').disable();
      }
      if (!this.metadataForm.get('isDependent').value) {
        this.metadataForm.get('dependentOnAttributeId').disable();
      }
      this.viewMode = false;
    }
    this.scrollToTop();
  }

  async selectedContractFrom(val: any, data, view) {
    await this.service.getAContract(val).then((res: any) => {
      this.contractFroms = res.attributes as Array<object>;
    })
    if (this.contractFroms) {
      const selectedInheritContractFrom = this.contractFroms.find(option => option.attributeId == data.inheritedFrom);
      if (selectedInheritContractFrom) {
        this.metadataForm.get('inheritedFrom').setValue(selectedInheritContractFrom.attributeId);
        if (view)
          this.metadataForm.get('inheritedFrom').disable();
        else
          this.metadataForm.get('inheritedFrom').enable();
      }
    }
  }


  async saveContract() {
    if (this.contractForm.valid && this.metadataForm.valid) {
      this.isLoading = true;
      if (this.metadataForm.get('metaDataValues').value)
        this.metadataForm.get('metaDataValues').setValue(this.values.join(','));
      const data = { ...this.contractForm.value, attributes: [{ ...this.metadataForm.value }] }

      if (this.id) {
        await this.service.editContract(data, this.contractType).then((res: any) => {
          this.contractType = res;
          this.toast.success('Data has been updated successfully', '');
        });
      } else {
        await this.service.saveContract(data).then((res: any) => {
          this.contractType = res;
          this.id = res.contractTypeId;
          this.toast.success('Data has been added successfully', '');
        });
      }
      this.isLoading = false;
      this.metadataForm.reset();
      this.initializeContract();
    }
  }

  reset() {
    this.metadataForm.reset();
    this.metadataForm.get('dependentOnAttributeId').disable();
    this.metadataForm.get('dependentOnAttributeId').setValue(null);
    this.metadataForm.get('dependentOnAttributeId').setValidators(null);
    this.metadataForm.get('dependentOnAttributeId').updateValueAndValidity();
    this.resetToggleInherit();
    this.metaDataList = this.allMetaDataList;
    if (this.attributeSequenceNumber) {
      this.metadataForm.get('sequenceNo').setValue(this.attributeSequenceNumber + 1);
    }
  }

  async activate() {
    this.isLoading = true;
    this.contractType.name = this.contractForm.get('name').value;
    this.contractType.description = this.contractForm.get('description').value;
    await this.service.activateContract(this.contractType, this.seedAttributes).then(res => {
      this.toast.success("Contract Type Activated Succesfully!", '');
      this.reset();
      this.initializeContract();
      this.router.navigate(['/app/contract-type/list']);
    });
    this.isLoading = false;
  }

  validateSequenceNumber() {
    if (this.metadataForm.get('sequenceNo').value) {
      let attributeList = [];
      if (this.metaDataList && this.seedAttributes) {
        attributeList = [...this.metaDataList, ...this.seedAttributes];
      } else if (this.metaDataList) {
        attributeList = this.metaDataList;
      } else if (this.seedAttributes) {
        attributeList = this.seedAttributes;
      } else {
        attributeList = [];
      }
      const isSequenceNoExist = attributeList.some((attribute: Attributes) => {
        return attribute.sequenceNo === this.metadataForm.get('sequenceNo').value;
      });

      this.isSequenceNumberValid = !isSequenceNoExist;
      if (!isSequenceNoExist) {
        this.metadataForm.get('sequenceNo').setErrors(null);
        this.metadataForm.get('sequenceNo')?.setValidators([Validators.required, Validators.min(1)]);
        this.metadataForm.get('sequenceNo')?.updateValueAndValidity();

      } else {
        this.metadataForm.get('sequenceNo').setErrors({ 'incorrect': true });
      }
    } else {
      this.isSequenceNumberValid = true;
      this.metadataForm.get('sequenceNo').setErrors(null);
      this.metadataForm.get('sequenceNo')?.setValidators([Validators.required, Validators.min(1)]);
      this.metadataForm.get('sequenceNo')?.updateValueAndValidity();
    }
  }

  openDeleteConfirmation(id): void {
    const dialogRef =
      this.dialog.open(DeleteDialogComponent, {
        enterAnimationDuration: '400ms',
        exitAnimationDuration: '500ms',
        data: {
          deleteFunction: () => this.delete(id, dialogRef)
        }
      });
  }

  async delete(attributeId: number, dialogRef: MatDialogRef<DeleteDialogComponent>) {
    try {
      await this.service.deleteAttribute(attributeId, this.id);
      this.toast.success('The attribute has been deleted successfully!', '');
      this.reset();
      this.initializeContract();
      dialogRef.close();
    } catch (error) {
      this.toast.error('Failed to delete the attribute!', '');
    }
  }

  getDropdownLabel(): string {
    switch (this.metadataForm.get('metaDataTypeId').value) {
      case 2:
        return 'Dropdown Values';
      case 15:
        return 'Multi Dropdown Values';
      case 12:
        return 'Boolean Values';
      default:
        return '';
    }
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value) {
      if (this.metadataForm.get('metaDataTypeId').value == 12) {
        if (this.values.length < 2) {
          this.isBooleanError = false;
          this.values.push(value);
        } else {
          this.isBooleanError = true;
          this.metadataForm.get('metaDataValues').setErrors({ 'incorrect': true });
        }
      } else {
        this.isBooleanError = false;
        this.values.push(value);
      }
    }

    event.chipInput!.clear();
  }

  remove(value): void {
    const index = this.values.indexOf(value);
    this.isBooleanError = false;
    if (index >= 0) {
      this.values.splice(index, 1);
      this.announcer.announce(`Removed ${value}`);
    }
  }

  edit(val, event: MatChipEditedEvent) {
    const value = event.value.trim();
    this.isBooleanError = false;
    if (!value) {
      this.remove(val);
      return;
    }

    const index = this.values.indexOf(val);
    if (index >= 0) {
      this.values[index] = value;
    }
  }

  applyFilter(filterValue: string) {
    filterValue = filterValue.trim();
    filterValue = filterValue.toLowerCase();
    this.dataSource.filter = filterValue;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  customSortData(event: Sort) {
    const data = this.dataSource.data.slice();
    if (!event.active || event.direction === '') {
      this.dataSource.data = data;
      return;
    }

    this.dataSource.data = data.sort((a, b) => {
      const valueA = (a[event.active] || '').toString().toLowerCase();
      const valueB = (b[event.active] || '').toString().toLowerCase();

      return this.compare(valueA, valueB, event.direction === 'asc');
    });

    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  compare(a: string, b: string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

}
